import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Zap, Plus, Edit, Trash2, Eye, Users, Clock, Target,
  ArrowLeft, Play, StopCircle, Trophy, CheckCircle, X, Loader,
  Calendar, Star, Code, Award, Crown, DollarSign, Tag, Menu, LogOut,
  Building2, LayoutDashboard, Briefcase, ClipboardList, UserPlus, Search, BarChart3, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { CompanyBrand } from '../components/company/CompanyBrand';
import { toast } from 'sonner';
import { load } from '@cashfreepayments/cashfree-js';
import ShareButtons from '../components/ShareButtons';
import { getFrontendUrl } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;

const difficultyColors = {
  easy: 'bg-green-100 text-green-700 border-green-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  hard: 'bg-red-100 text-red-700 border-red-200'
};

const statusColors = {
  upcoming: 'bg-blue-100 text-blue-700',
  live: 'bg-green-100 text-green-700',
  ended: 'bg-gray-100 text-gray-600'
};

const CompanyHiringChallengesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showNationalModal, setShowNationalModal] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [problems, setProblems] = useState([]);
  const [selectedJobForNational, setSelectedJobForNational] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  
  // Dynamic pricing and coupon state
  const [nationalChallengePrice, setNationalChallengePrice] = useState(9999);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    challenge_type: 'coding',
    duration_minutes: 60,
    start_time: '',
    end_time: '',
    problems: [],
    mcqs: [],  // Direct MCQ IDs for this challenge
    skills_tested: [],
    difficulty: 'medium',
    max_participants: '',
    is_public: true,  // Default to public
    job_id: '',
    // Assessment linking
    assessment_option: 'none', // 'none', 'existing', 'new'
    assessment_id: '',
    new_assessment_title: '',
    new_assessment_questions: []
  });
  
  const [skillInput, setSkillInput] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [newQuestion, setNewQuestion] = useState({ question: '', options: ['', '', '', ''], correct_answer: 0 });
  const token = localStorage.getItem('company_token');

  // Navigation items for sidebar
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/company/dashboard' },
    { label: 'Job Postings', icon: Briefcase, path: '/company/jobs' },
    { label: 'Assessments', icon: ClipboardList, path: '/company/assessments' },
    { label: 'Hiring Challenges', icon: Trophy, path: '/company/hiring-challenges', active: true },
    { label: 'Enterprise', icon: Crown, path: '/company/enterprise' },
    { label: 'Events', icon: Calendar, path: '/company/events' },
    { label: 'Candidate Pool', icon: UserPlus, path: '/company/candidates' },
    { label: 'Talent Search', icon: Search, path: '/company/talent-search' },
    { label: 'Shortlisted', icon: Star, path: '/company/shortlisted' },
    { label: 'Analytics', icon: BarChart3, path: '/company/analytics' },
    { label: 'Subscription', icon: CreditCard, path: '/company/subscription' },
  ];

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    
    const company = localStorage.getItem('company_data');
    if (company) {
      setCompanyData(JSON.parse(company));
    }
    
    fetchChallenges();
    fetchJobs();
    fetchProblems();
    fetchAssessments();
    fetchPricing();
    
    // Check for national challenge creation from query param
    if (searchParams.get('create') === 'national') {
      setShowNationalModal(true);
    }
    
    // Handle payment callback
    const orderId = searchParams.get('order_id');
    const paymentStatus = searchParams.get('status');
    if (orderId && paymentStatus) {
      handlePaymentCallback(orderId, paymentStatus);
    }
  }, [token, navigate, searchParams]);

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
    toast.success('Logged out successfully');
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/public/company-pricing`);
      if (res.data.pricing?.national_challenge_price) {
        setNationalChallengePrice(res.data.pricing.national_challenge_price);
      }
    } catch (err) {
      console.error('Failed to fetch pricing');
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const res = await axios.post(`${API}/api/public/validate-company-coupon`, {
        coupon_code: couponCode.trim()
      });
      
      setAppliedCoupon(res.data);
      setCouponError('');
      toast.success(res.data.message);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handlePaymentCallback = async (orderId, status) => {
    try {
      await axios.post(
        `${API}/api/company/competitions/verify-payment`,
        null,
        {
          params: { order_id: orderId, payment_status: status },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (status === 'success') {
        toast.success('National Challenge activated! It will now appear on EvalentX Arena.');
        fetchJobs();
      } else {
        toast.error('Payment failed. Please try again.');
      }
      navigate('/company/hiring-challenges', { replace: true });
    } catch (err) {
      toast.error('Failed to verify payment');
    }
  };

  const initiateNationalPayment = async (jobId) => {
    setPaymentLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/company/competitions/initiate-payment`,
        null,
        {
          params: { job_id: jobId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.data.payment_session_id) {
        const cashfree = await load({ mode: 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          returnUrl: `${getFrontendUrl()}/company/hiring-challenges?order_id=${res.data.order_id}&status=success`
        });
      } else {
        // Mock flow for demo
        toast.info('Payment gateway simulating... Please wait.');
        await axios.post(
          `${API}/api/company/competitions/verify-payment`,
          null,
          {
            params: { order_id: res.data.order_id, payment_status: 'success' },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('National Challenge activated! It will now appear on EvalentX Arena.');
        fetchJobs();
        setShowNationalModal(false);
        setSelectedJobForNational(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchChallenges = async () => {
    try {
      const res = await axios.get(`${API}/api/company/hiring-challenges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(res.data.challenges || []);
    } catch (err) {
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/api/company/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs');
    }
  };

  const fetchProblems = async () => {
    try {
      const res = await axios.get(`${API}/api/codearena/problems`);
      setProblems(res.data.problems || []);
    } catch (err) {
      console.error('Failed to load problems');
    }
  };

  const fetchAssessments = async () => {
    try {
      const res = await axios.get(`${API}/api/company/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(res.data.assessments || []);
    } catch (err) {
      console.error('Failed to load assessments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_time) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        challenge_type: form.challenge_type,
        duration_minutes: form.duration_minutes,
        start_time: form.start_time,
        end_time: form.end_time,
        problems: form.problems,
        mcqs: form.mcqs || [],  // Direct MCQ IDs
        skills_tested: form.skills_tested,
        difficulty: form.difficulty,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        is_public: form.is_public,
        job_id: form.job_id || null,
        // Assessment linking
        assessment_id: form.assessment_option === 'existing' ? form.assessment_id : null,
        create_new_assessment: form.assessment_option === 'new',
        new_assessment_title: form.assessment_option === 'new' ? form.new_assessment_title : null,
        new_assessment_questions: form.assessment_option === 'new' ? form.new_assessment_questions : null
      };
      
      if (editingChallenge) {
        await axios.put(`${API}/api/company/hiring-challenges/${editingChallenge.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Challenge updated');
      } else {
        await axios.post(`${API}/api/company/hiring-challenges`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Challenge created and will appear on EvalentX Arena!');
      }
      
      setShowModal(false);
      resetForm();
      fetchChallenges();
      fetchAssessments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save challenge');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (challengeId) => {
    if (!window.confirm('Delete this hiring challenge?')) return;
    
    try {
      await axios.delete(`${API}/api/company/hiring-challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge deleted');
      fetchChallenges();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleEndChallenge = async (challengeId) => {
    if (!window.confirm('End this challenge and finalize rankings?')) return;
    
    try {
      await axios.post(`${API}/api/company/hiring-challenges/${challengeId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Challenge ended');
      fetchChallenges();
    } catch (err) {
      toast.error('Failed to end challenge');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      challenge_type: 'coding',
      duration_minutes: 60,
      start_time: '',
      end_time: '',
      problems: [],
      skills_tested: [],
      difficulty: 'medium',
      max_participants: '',
      is_public: true,
      job_id: '',
      assessment_option: 'none',
      assessment_id: '',
      new_assessment_title: '',
      new_assessment_questions: []
    });
    setNewQuestion({ question: '', options: ['', '', '', ''], correct_answer: 0 });
    setEditingChallenge(null);
    setSkillInput('');
  };

  const openEditModal = (challenge) => {
    setEditingChallenge(challenge);
    setForm({
      title: challenge.title || '',
      description: challenge.description || '',
      challenge_type: challenge.challenge_type || 'coding',
      duration_minutes: challenge.duration_minutes || 60,
      start_time: challenge.start_time?.slice(0, 16) || '',
      end_time: challenge.end_time?.slice(0, 16) || '',
      problems: challenge.problems || [],
      mcqs: challenge.mcqs || [],
      skills_tested: challenge.skills_tested || [],
      difficulty: challenge.difficulty || 'medium',
      max_participants: challenge.max_participants?.toString() || '',
      is_public: challenge.is_public || false,
      job_id: challenge.job_id || '',
      assessment_option: challenge.assessment_id ? 'existing' : 'none',
      assessment_id: challenge.assessment_id || '',
      new_assessment_title: '',
      new_assessment_questions: []
    });
    setShowModal(true);
  };

  const addSkill = () => {
    if (skillInput.trim() && !form.skills_tested.includes(skillInput.trim())) {
      setForm(prev => ({
        ...prev,
        skills_tested: [...prev.skills_tested, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setForm(prev => ({
      ...prev,
      skills_tested: prev.skills_tested.filter(s => s !== skill)
    }));
  };

  const toggleProblem = (problemId) => {
    setForm(prev => ({
      ...prev,
      problems: prev.problems.includes(problemId)
        ? prev.problems.filter(p => p !== problemId)
        : [...prev.problems, problemId]
    }));
  };

  const getStatusFromChallenge = (challenge) => {
    const now = new Date().toISOString();
    if (challenge.end_time && now > challenge.end_time) return 'ended';
    if (challenge.start_time && now >= challenge.start_time) return 'live';
    return 'upcoming';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f2ef]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-[#666666]">Loading Challenges...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#f3f2ef]">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-out
        lg:transform-none h-screen overflow-y-auto
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 sticky top-0 bg-white z-10">
          <Link to="/company" className="flex items-center gap-3">
            <CompanyBrand />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -mr-2"
            data-testid="close-sidebar-btn"
          >
            <X size={20} className="text-[#666666]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3">
          <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider px-3 mb-2">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                  ${item.active 
                    ? 'bg-[#e8f4f8] text-[#0a66c2] font-medium' 
                    : 'text-[#666666] hover:bg-gray-100 hover:text-[#191919]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} strokeWidth={item.active ? 2.5 : 2} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#b24020] hover:bg-red-50 rounded-lg transition-colors"
            data-testid="company-logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -ml-2"
              data-testid="open-sidebar-btn"
            >
              <Menu size={22} className="text-[#666666]" />
            </button>
            <div>
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider hidden sm:block">Hiring</p>
              <h1 className="text-lg sm:text-xl font-semibold text-[#191919]">Challenges</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors text-sm font-medium"
              data-testid="create-challenge-btn"
            >
              <Plus size={16} />
              <span className="hidden md:inline">Create Challenge</span>
            </button>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="sm:hidden p-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors"
              data-testid="create-challenge-btn-mobile"
            >
              <Plus size={20} />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-total-challenges">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#fff7ed] rounded-lg flex items-center justify-center">
                  <Zap size={16} className="text-[#b24020]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Total</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#191919]">{challenges.length}</p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Challenges</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-live-challenges">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#057642]/10 rounded-lg flex items-center justify-center">
                  <Play size={16} className="text-[#057642]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Live</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#057642]">
                {challenges.filter(c => getStatusFromChallenge(c) === 'live').length}
              </p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Active Now</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-participants">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#e8f4f8] rounded-lg flex items-center justify-center">
                  <Users size={16} className="text-[#0a66c2]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Users</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#0a66c2]">
                {challenges.reduce((a, c) => a + (c.participants_count || 0), 0)}
              </p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Participants</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-submissions">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#f3f2ef] rounded-lg flex items-center justify-center">
                  <Target size={16} className="text-[#666666]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Subs</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#191919]">
                {challenges.reduce((a, c) => a + (c.submissions_count || 0), 0)}
              </p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Submissions</p>
            </div>
          </div>

        {/* National Hiring Challenge Promotion */}
        <div className="mb-6 sm:mb-8 bg-gradient-to-r from-[#b24020] to-[#0a66c2] rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Crown size={24} />
                <h3 className="text-xl font-bold">Host a National Hiring Challenge</h3>
              </div>
              <p className="text-amber-100 max-w-lg mb-4">
                Get nationwide visibility for your hiring drive! Your challenge will be featured prominently on EvalentX Arena 
                and reach 50,000+ developers across India.
              </p>
              <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-amber-100 mb-4">
                <li className="flex items-center gap-1"><CheckCircle size={14} /> Featured on EvalentX Arena</li>
                <li className="flex items-center gap-1"><CheckCircle size={14} /> National visibility</li>
                <li className="flex items-center gap-1"><CheckCircle size={14} /> 30 days featured placement</li>
                <li className="flex items-center gap-1"><CheckCircle size={14} /> Priority in search results</li>
              </ul>
            </div>
            <div className="text-center flex-shrink-0">
              <p className="text-amber-200 text-sm mb-1">One-time fee</p>
              <p className="text-4xl font-bold mb-3">₹{nationalChallengePrice.toLocaleString()}</p>
              <button
                onClick={() => setShowNationalModal(true)}
                className="px-6 py-3 bg-white text-orange-600 rounded-xl font-semibold hover:bg-amber-50 transition-colors flex items-center gap-2 mx-auto"
                data-testid="go-national-promo-btn"
              >
                <Zap size={18} />
                Go National Now
              </button>
            </div>
          </div>
        </div>

        {/* Challenges List */}
        {challenges.length > 0 ? (
          <div className="space-y-4">
            {challenges.map((challenge) => {
              const status = getStatusFromChallenge(challenge);
              return (
                <div 
                  key={challenge.id}
                  className="bg-white rounded-xl border border-gray-200 p-6"
                  data-testid={`challenge-card-${challenge.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{challenge.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[status]}`}>
                          {status === 'live' ? '🔴 Live' : status === 'upcoming' ? '📅 Upcoming' : 'Ended'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${difficultyColors[challenge.difficulty]}`}>
                          {challenge.difficulty}
                        </span>
                        {challenge.is_public && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                            Public
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-500 text-sm mb-3 line-clamp-2">{challenge.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {challenge.duration_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={14} />
                          {challenge.participants_count || 0} participants
                        </span>
                        <span className="flex items-center gap-1">
                          <Code size={14} />
                          {challenge.problems?.length || 0} problems
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(challenge.start_time).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {challenge.skills_tested?.slice(0, 4).map((skill, idx) => (
                          <span key={idx} className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-lg">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {/* Share Button */}
                      <ShareButtons
                        title={challenge.title}
                        description={challenge.description}
                        url={`/codearena/hiring-challenges/${challenge.id}`}
                        shareText={`🏆 ${challenge.title} | Hiring Challenge | ${challenge.duration_minutes} min | Difficulty: ${challenge.difficulty} | Join now on EvalentX!`}
                        type="challenge"
                      />
                      <Link
                        to={`/company/hiring-challenges/${challenge.id}`}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="View Leaderboard"
                      >
                        <Trophy size={18} />
                      </Link>
                      {status !== 'ended' && (
                        <button
                          onClick={() => openEditModal(challenge)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                      )}
                      {status === 'live' && (
                        <button
                          onClick={() => handleEndChallenge(challenge.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="End Challenge"
                        >
                          <StopCircle size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(challenge.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Zap className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Hiring Challenges Yet</h3>
            <p className="text-gray-500 mb-4">Create your first coding challenge to discover top talent.</p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] font-medium"
            >
              <Plus size={18} />
              Create Your First Challenge
            </button>
          </div>
        )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingChallenge ? 'Edit Challenge' : 'Create Hiring Challenge'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Challenge Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Backend Developer Challenge"
                  required
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  placeholder="Describe the challenge and what you're looking for..."
                />
              </div>
              
              {/* Type, Difficulty, Duration */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.challenge_type}
                    onChange={(e) => setForm(prev => ({ ...prev, challenge_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="coding">Coding</option>
                    <option value="mcq">MCQ</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={form.difficulty}
                    onChange={(e) => setForm(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                  <input
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    min={15}
                    max={300}
                  />
                </div>
              </div>
              
              {/* Start Time & Max Participants */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                  <input
                    type="number"
                    value={form.max_participants}
                    onChange={(e) => setForm(prev => ({ ...prev, max_participants: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              </div>
              
              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skills Tested</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Type a skill and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.skills_tested.map((skill, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                      {skill}
                      <button type="button" onClick={() => removeSkill(skill)} className="hover:text-orange-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Link to Job */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Job Posting (Optional)</label>
                <select
                  value={form.job_id}
                  onChange={(e) => setForm(prev => ({ ...prev, job_id: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">No linked job</option>
                  {jobs.filter(j => j.is_active).map(job => (
                    <option key={job.id} value={job.id}>{job.title}</option>
                  ))}
                </select>
              </div>
              
              {/* Assessment Linking Section */}
              <div className="border border-purple-200 rounded-xl p-4 bg-purple-50/50">
                <label className="block text-sm font-semibold text-purple-800 mb-3">
                  📋 Assessment / Test Linking
                </label>
                
                <div className="space-y-3">
                  {/* Option Selection */}
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="assessment_option"
                        value="none"
                        checked={form.assessment_option === 'none'}
                        onChange={() => setForm(prev => ({ ...prev, assessment_option: 'none', assessment_id: '' }))}
                        className="text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">No Assessment</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="assessment_option"
                        value="existing"
                        checked={form.assessment_option === 'existing'}
                        onChange={() => setForm(prev => ({ ...prev, assessment_option: 'existing' }))}
                        className="text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Link Existing Assessment</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="assessment_option"
                        value="new"
                        checked={form.assessment_option === 'new'}
                        onChange={() => setForm(prev => ({ ...prev, assessment_option: 'new' }))}
                        className="text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Create New Assessment</span>
                    </label>
                  </div>
                  
                  {/* Existing Assessment Selection */}
                  {form.assessment_option === 'existing' && (
                    <div>
                      <select
                        value={form.assessment_id}
                        onChange={(e) => setForm(prev => ({ ...prev, assessment_id: e.target.value }))}
                        className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                        data-testid="existing-assessment-select"
                      >
                        <option value="">Select an assessment...</option>
                        {assessments.map(a => (
                          <option key={a.id} value={a.id}>{a.title} ({a.questions?.length || 0} questions)</option>
                        ))}
                      </select>
                      {assessments.length === 0 && (
                        <p className="text-sm text-purple-600 mt-1">No assessments found. Create one first or choose "Create New Assessment".</p>
                      )}
                    </div>
                  )}
                  
                  {/* New Assessment Creation */}
                  {form.assessment_option === 'new' && (
                    <div className="space-y-3 p-3 bg-white rounded-lg border border-purple-200">
                      <input
                        type="text"
                        value={form.new_assessment_title}
                        onChange={(e) => setForm(prev => ({ ...prev, new_assessment_title: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Assessment Title"
                        data-testid="new-assessment-title"
                      />
                      
                      <div className="text-sm font-medium text-gray-700">
                        Questions ({form.new_assessment_questions.length} added)
                      </div>
                      
                      {/* Question list */}
                      {form.new_assessment_questions.length > 0 && (
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {form.new_assessment_questions.map((q, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                              <span className="truncate flex-1">{idx + 1}. {q.question}</span>
                              <button
                                type="button"
                                onClick={() => setForm(prev => ({
                                  ...prev,
                                  new_assessment_questions: prev.new_assessment_questions.filter((_, i) => i !== idx)
                                }))}
                                className="text-red-500 hover:text-red-700 ml-2"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add question form */}
                      <div className="space-y-2 p-2 border border-dashed border-purple-300 rounded">
                        <input
                          type="text"
                          value={newQuestion.question}
                          onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          placeholder="Question text"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          {newQuestion.options.map((opt, idx) => (
                            <input
                              key={idx}
                              type="text"
                              value={opt}
                              onChange={(e) => {
                                const newOpts = [...newQuestion.options];
                                newOpts[idx] = e.target.value;
                                setNewQuestion(prev => ({ ...prev, options: newOpts }));
                              }}
                              className={`px-3 py-1 border rounded text-sm ${
                                newQuestion.correct_answer === idx ? 'border-green-500 bg-green-50' : 'border-gray-300'
                              }`}
                              placeholder={`Option ${idx + 1}${newQuestion.correct_answer === idx ? ' (Correct)' : ''}`}
                            />
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <select
                            value={newQuestion.correct_answer}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, correct_answer: parseInt(e.target.value) }))}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value={0}>Option 1 is correct</option>
                            <option value={1}>Option 2 is correct</option>
                            <option value={2}>Option 3 is correct</option>
                            <option value={3}>Option 4 is correct</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              if (newQuestion.question && newQuestion.options.every(o => o)) {
                                setForm(prev => ({
                                  ...prev,
                                  new_assessment_questions: [...prev.new_assessment_questions, newQuestion]
                                }));
                                setNewQuestion({ question: '', options: ['', '', '', ''], correct_answer: 0 });
                              }
                            }}
                            className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                          >
                            Add Question
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Problems Selection */}
              {problems.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Problems ({form.problems.length} selected)
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {problems.map(problem => (
                      <label key={problem.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.problems.includes(problem.id)}
                          onChange={() => toggleProblem(problem.id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="flex-1 text-sm">{problem.title}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          problem.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                          problem.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {problem.difficulty}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* MCQ Section - Link Assessment or Create New */}
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-sm font-medium text-gray-700">
                  MCQ Questions (Optional)
                </label>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="mcq_option"
                      checked={form.assessment_option === 'none'}
                      onChange={() => setForm(prev => ({ ...prev, assessment_option: 'none', assessment_id: '' }))}
                      className="text-blue-600"
                    />
                    <span className="text-sm">No MCQs - Coding problems only</span>
                  </label>
                  
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="mcq_option"
                      checked={form.assessment_option === 'existing'}
                      onChange={() => setForm(prev => ({ ...prev, assessment_option: 'existing' }))}
                      className="text-blue-600"
                    />
                    <span className="text-sm">Link existing assessment (MCQs will be imported)</span>
                  </label>
                  
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="mcq_option"
                      checked={form.assessment_option === 'new'}
                      onChange={() => setForm(prev => ({ ...prev, assessment_option: 'new' }))}
                      className="text-blue-600"
                    />
                    <span className="text-sm">Create new MCQs for this challenge</span>
                  </label>
                </div>
                
                {/* Existing Assessment Dropdown */}
                {form.assessment_option === 'existing' && (
                  <div className="mt-3">
                    <select
                      value={form.assessment_id}
                      onChange={(e) => setForm(prev => ({ ...prev, assessment_id: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select an assessment...</option>
                      {assessments.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.title} ({a.total_mcqs || 0} MCQs)
                        </option>
                      ))}
                    </select>
                    {assessments.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        No assessments found. <a href="/company/assessments" className="text-blue-600 underline">Create one first</a>
                      </p>
                    )}
                  </div>
                )}
                
                {/* New MCQ Creation */}
                {form.assessment_option === 'new' && (
                  <div className="mt-3 space-y-3">
                    <input
                      type="text"
                      placeholder="Assessment Title (e.g., Technical MCQs)"
                      value={form.new_assessment_title}
                      onChange={(e) => setForm(prev => ({ ...prev, new_assessment_title: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    />
                    
                    {/* Questions List */}
                    {form.new_assessment_questions.length > 0 && (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {form.new_assessment_questions.map((q, idx) => (
                          <div key={idx} className="p-2 bg-white rounded border text-sm flex justify-between items-start">
                            <div>
                              <div className="font-medium">Q{idx + 1}: {q.question}</div>
                              <div className="text-xs text-gray-500">
                                Options: {q.options.filter(o => o).join(', ')}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setForm(prev => ({
                                ...prev,
                                new_assessment_questions: prev.new_assessment_questions.filter((_, i) => i !== idx)
                              }))}
                              className="text-red-500 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add Question Form */}
                    <div className="p-3 bg-white rounded-lg border space-y-2">
                      <input
                        type="text"
                        placeholder="Question text"
                        value={newQuestion.question}
                        onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                        className="w-full p-2 border border-gray-200 rounded text-sm"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        {[0, 1, 2, 3].map(i => (
                          <input
                            key={i}
                            type="text"
                            placeholder={`Option ${String.fromCharCode(65 + i)}`}
                            value={newQuestion.options[i]}
                            onChange={(e) => {
                              const opts = [...newQuestion.options];
                              opts[i] = e.target.value;
                              setNewQuestion(prev => ({ ...prev, options: opts }));
                            }}
                            className="p-2 border border-gray-200 rounded text-sm"
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Correct:</span>
                        <select
                          value={newQuestion.correct_answer}
                          onChange={(e) => setNewQuestion(prev => ({ ...prev, correct_answer: parseInt(e.target.value) }))}
                          className="p-1 border border-gray-200 rounded text-sm"
                        >
                          <option value={0}>A</option>
                          <option value={1}>B</option>
                          <option value={2}>C</option>
                          <option value={3}>D</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            if (newQuestion.question && newQuestion.options.some(o => o)) {
                              setForm(prev => ({
                                ...prev,
                                new_assessment_questions: [...prev.new_assessment_questions, { ...newQuestion }]
                              }));
                              setNewQuestion({ question: '', options: ['', '', '', ''], correct_answer: 0 });
                            }
                          }}
                          className="ml-auto px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                        >
                          Add Question
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Public Toggle */}
              <label className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm(prev => ({ ...prev, is_public: e.target.checked }))}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">
                  <strong>Publish on EvalentX Arena</strong> - Make this challenge visible to all students on EvalentX Arena landing page and student dashboard
                </span>
              </label>
              
              {/* Submit */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingChallenge ? 'Update' : 'Create Challenge')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* National Challenge Modal */}
      {showNationalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Crown className="text-white" size={32} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Host National Hiring Challenge</h2>
              <p className="text-gray-500">
                Select a job posting to upgrade to a national hiring competition
              </p>
            </div>
            
            {/* Job Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Job/Internship</label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {jobs.filter(job => job.is_active && !job.is_national_competition).length > 0 ? (
                  jobs.filter(job => job.is_active && !job.is_national_competition).map((job) => (
                    <button
                      key={job.id}
                      onClick={() => setSelectedJobForNational(job)}
                      className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                        selectedJobForNational?.id === job.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <p className="font-medium text-gray-900">{job.title}</p>
                      <p className="text-sm text-gray-500">{job.location} • {job.job_type}</p>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <p>No eligible jobs available.</p>
                    <Link to="/company/jobs" className="text-amber-600 hover:underline text-sm mt-2 inline-block">
                      Create a job posting first →
                    </Link>
                  </div>
                )}
              </div>
            </div>
            
            {/* Pricing Info */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="text-center mb-4">
                {appliedCoupon ? (
                  <div>
                    <span className="text-xl text-gray-400 line-through">₹{nationalChallengePrice.toLocaleString()}</span>
                    <span className="text-3xl font-bold text-green-600 ml-2">₹{appliedCoupon.final_price.toLocaleString()}</span>
                    <span className="text-gray-500">/one-time</span>
                    <p className="text-green-600 text-sm mt-1">{appliedCoupon.message}</p>
                  </div>
                ) : (
                  <div>
                    <span className="text-3xl font-bold text-amber-600">₹{nationalChallengePrice.toLocaleString()}</span>
                    <span className="text-gray-500">/one-time</span>
                  </div>
                )}
              </div>
              
              {/* Coupon Code Section */}
              <div className="mb-4 p-3 bg-white/60 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Have a coupon code?</label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-green-700 font-medium">{appliedCoupon.coupon_code}</span>
                    <button onClick={removeCoupon} className="text-gray-500 hover:text-red-500">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      data-testid="coupon-input"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 font-medium disabled:opacity-50"
                    >
                      {couponLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                {couponError && <p className="text-red-500 text-sm mt-1">{couponError}</p>}
              </div>
              
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                  <span>Featured on EvalentX Arena Upcoming Contests</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                  <span>National visibility across all colleges</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                  <span>"National Competition" badge</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                  <span>30 days featured placement</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                  <span>Company branding (logo, banner, poster)</span>
                </li>
              </ul>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowNationalModal(false); setSelectedJobForNational(null); removeCoupon(); }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
                disabled={paymentLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => selectedJobForNational && initiateNationalPayment(selectedJobForNational.id)}
                disabled={!selectedJobForNational || paymentLoading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-national-btn"
              >
                {paymentLoading ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Processing...
                  </>
                ) : (
                  <>
                    <DollarSign size={18} />
                    Pay ₹{(appliedCoupon?.final_price || nationalChallengePrice).toLocaleString()}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyHiringChallengesPage;
